import { gsap } from "gsap";
import { Draggable } from "gsap/Draggable";
import { InertiaPlugin } from "gsap/InertiaPlugin";

gsap.registerPlugin(Draggable, InertiaPlugin);

const BREAKPOINTS = [
    { minWidth: 1200, slidesPerView: 4 },
    { minWidth: 768, slidesPerView: 3 },
    { minWidth: 576, slidesPerView: 2 },
    { minWidth: 0, slidesPerView: 1 },
];

const TRANSITION_DURATION = 0.4;
const CLONE_FLAG = "data-carousel-clone";

const slidesPerViewFor = function (width) {
    return BREAKPOINTS.find(function (breakpoint) {
        return width >= breakpoint.minWidth;
    }).slidesPerView;
};

class Carousel {
    constructor(element, options) {
        this.element = element;
        this.wrapper = element.querySelector(".swiper-wrapper");
        if (!this.wrapper) return;

        this.realSlides = Array.from(this.wrapper.children).filter(function (child) {
            return child.classList.contains("swiper-slide");
        });
        if (this.realSlides.length === 0) return;

        this.loop = options.loop === true;
        this.autoplayDelay = options.autoplayDelay || 0;
        this.previousButton = element.querySelector(".swiper-button-prev");
        this.nextButton = element.querySelector(".swiper-button-next");
        this.paginationElement = options.pagination
            ? element.querySelector(".swiper-pagination")
            : null;
        this.scrollbarElement = options.scrollbar
            ? element.querySelector(".swiper-scrollbar")
            : null;

        this.position = 0;
        this.slidesPerView = 0;
        this.slideWidth = 0;
        this.autoplayTimer = null;
        this.autoplayPaused = false;

        this.element.classList.add("swiper-horizontal");

        this.bindControls();
        this.build();

        this.resizeTimer = null;
        window.addEventListener(
            "resize",
            function () {
                window.clearTimeout(this.resizeTimer);
                this.resizeTimer = window.setTimeout(this.build.bind(this), 150);
            }.bind(this),
        );
    }

    get renderedSlides() {
        return Array.from(this.wrapper.children).filter(function (child) {
            return child.classList.contains("swiper-slide");
        });
    }

    get cloneCount() {
        return this.loop ? this.slidesPerView : 0;
    }

    get lastPosition() {
        if (this.loop) return this.realSlides.length + this.cloneCount - 1;
        return Math.max(0, this.realSlides.length - this.slidesPerView);
    }

    get dragLimit() {
        if (this.loop) return this.realSlides.length + this.cloneCount;
        return this.lastPosition;
    }

    get realIndex() {
        const total = this.realSlides.length;
        return ((this.position - this.cloneCount) % total + total) % total;
    }

    build() {
        const width = this.element.clientWidth;
        if (width === 0) return;

        const slidesPerView = Math.min(
            slidesPerViewFor(window.innerWidth),
            this.realSlides.length,
        );
        const previousRealIndex = this.slidesPerView > 0 ? this.realIndex : 0;

        if (slidesPerView !== this.slidesPerView) {
            this.slidesPerView = slidesPerView;
            this.rebuildClones();
        }

        this.slidesPerView = slidesPerView;
        this.slideWidth = width / slidesPerView;

        this.renderedSlides.forEach(
            function (slide) {
                slide.style.width = this.slideWidth + "px";
                slide.style.flexShrink = "0";
            }.bind(this),
        );

        this.position = this.loop
            ? this.cloneCount + previousRealIndex
            : Math.min(previousRealIndex, this.lastPosition);

        this.moveTo(this.position, false);
        this.buildPagination();
        this.buildScrollbar();
        this.enableDrag();
        this.restartAutoplay();
    }

    rebuildClones() {
        this.renderedSlides.forEach(function (slide) {
            if (slide.hasAttribute(CLONE_FLAG)) slide.remove();
        });

        if (!this.loop) return;

        const count = Math.min(this.slidesPerView, this.realSlides.length);
        const firstRealSlide = this.realSlides[0];

        this.realSlides.slice(-count).forEach(
            function (slide) {
                const clone = slide.cloneNode(true);
                clone.setAttribute(CLONE_FLAG, "");
                this.wrapper.insertBefore(clone, firstRealSlide);
            }.bind(this),
        );

        this.realSlides.slice(0, count).forEach(
            function (slide) {
                const clone = slide.cloneNode(true);
                clone.setAttribute(CLONE_FLAG, "");
                this.wrapper.appendChild(clone);
            }.bind(this),
        );
    }

    moveTo(position, animate) {
        this.position = position;
        const x = -position * this.slideWidth;

        gsap.killTweensOf(this.wrapper);

        if (!animate) {
            gsap.set(this.wrapper, { x: x });
            this.afterMove();
            return;
        }

        gsap.to(this.wrapper, {
            x: x,
            duration: TRANSITION_DURATION,
            ease: "power2.out",
            onComplete: this.normalise.bind(this),
        });
        this.afterMove();
    }

    normalise() {
        if (!this.loop) return;

        const total = this.realSlides.length;
        let position = this.position;

        if (position >= total + this.cloneCount) position -= total;
        else if (position < this.cloneCount) position += total;
        else return;

        this.position = position;
        gsap.set(this.wrapper, { x: -position * this.slideWidth });
        this.afterMove();
    }

    afterMove() {
        this.updateNavigation();
        this.updatePagination();
        this.updateScrollbar();
    }

    next() {
        if (!this.loop && this.position >= this.lastPosition) return;
        this.moveTo(this.position + 1, true);
    }

    previous() {
        if (!this.loop && this.position <= 0) return;
        this.moveTo(this.position - 1, true);
    }

    bindControls() {
        if (this.previousButton)
            this.previousButton.addEventListener(
                "click",
                function () {
                    this.previous();
                    this.restartAutoplay();
                }.bind(this),
            );

        if (this.nextButton)
            this.nextButton.addEventListener(
                "click",
                function () {
                    this.next();
                    this.restartAutoplay();
                }.bind(this),
            );

        this.element.addEventListener(
            "mouseenter",
            function () {
                this.autoplayPaused = true;
            }.bind(this),
        );

        this.element.addEventListener(
            "mouseleave",
            function () {
                this.autoplayPaused = false;
            }.bind(this),
        );
    }

    updateNavigation() {
        if (this.loop) return;

        if (this.previousButton)
            this.previousButton.classList.toggle("swiper-button-disabled", this.position <= 0);

        if (this.nextButton)
            this.nextButton.classList.toggle(
                "swiper-button-disabled",
                this.position >= this.lastPosition,
            );
    }

    buildPagination() {
        if (!this.paginationElement) return;

        const pages = this.loop
            ? this.realSlides.length
            : this.lastPosition + 1;

        if (this.paginationElement.children.length === pages) return;

        this.paginationElement.innerHTML = "";
        this.paginationElement.classList.add("swiper-pagination-bullets");

        for (let page = 0; page < pages; page++) {
            const bullet = document.createElement("span");
            bullet.className = "swiper-pagination-bullet";
            bullet.setAttribute("role", "button");
            bullet.setAttribute("tabindex", "0");
            bullet.setAttribute("aria-label", page + 1);
            bullet.addEventListener(
                "click",
                function () {
                    this.moveTo(page + this.cloneCount, true);
                    this.restartAutoplay();
                }.bind(this),
            );
            this.paginationElement.appendChild(bullet);
        }

        this.updatePagination();
    }

    updatePagination() {
        if (!this.paginationElement) return;

        const active = this.loop ? this.realIndex : this.position;

        Array.from(this.paginationElement.children).forEach(function (bullet, index) {
            bullet.classList.toggle("swiper-pagination-bullet-active", index === active);
        });
    }

    buildScrollbar() {
        if (!this.scrollbarElement) return;

        if (!this.scrollbarDrag) {
            this.scrollbarDrag = document.createElement("div");
            this.scrollbarDrag.className = "swiper-scrollbar-drag";
            this.scrollbarElement.appendChild(this.scrollbarDrag);
            this.enableScrollbarDrag();
        }

        const visible = Math.min(1, this.slidesPerView / this.realSlides.length);
        this.scrollbarDrag.style.width = visible * 100 + "%";

        if (this.scrollbarDraggable) this.scrollbarDraggable[0].applyBounds(this.scrollbarElement);

        this.updateScrollbar();
    }

    updateScrollbar() {
        if (!this.scrollbarElement || !this.scrollbarDrag) return;
        if (this.scrollbarDragging) return;

        const track = this.scrollbarElement.clientWidth;
        const travel = track - this.scrollbarDrag.offsetWidth;
        const progress = this.lastPosition > 0 ? this.position / this.lastPosition : 0;

        gsap.set(this.scrollbarDrag, { x: travel * progress });
    }

    enableScrollbarDrag() {
        const carousel = this;

        this.scrollbarDraggable = Draggable.create(this.scrollbarDrag, {
            type: "x",
            cursor: "grab",
            activeCursor: "grabbing",
            bounds: this.scrollbarElement,
            onPress: function () {
                carousel.scrollbarDragging = true;
                carousel.autoplayPaused = true;
            },
            onDrag: function () {
                const travel = this.maxX - this.minX;
                const progress = travel > 0 ? (this.x - this.minX) / travel : 0;
                const target = Math.round(progress * carousel.lastPosition);

                carousel.position = target;
                gsap.set(carousel.wrapper, { x: -target * carousel.slideWidth });
                carousel.updateNavigation();
                carousel.updatePagination();
            },
            onRelease: function () {
                carousel.scrollbarDragging = false;
                carousel.autoplayPaused = false;
                carousel.updateScrollbar();
            },
        });
    }

    enableDrag() {
        if (this.draggable) {
            this.draggable[0].kill();
            this.draggable = null;
        }

        const carousel = this;

        this.draggable = Draggable.create(this.wrapper, {
            type: "x",
            cursor: "grab",
            activeCursor: "grabbing",
            inertia: true,
            edgeResistance: 0.9,
            bounds: {
                minX: -this.dragLimit * this.slideWidth,
                maxX: 0,
            },
            snap: {
                x: function (value) {
                    return Math.round(value / carousel.slideWidth) * carousel.slideWidth;
                },
            },
            onPress: function () {
                carousel.autoplayPaused = true;
                gsap.killTweensOf(carousel.wrapper);
            },
            onDragEnd: function () {
                if (!this.isThrowing) carousel.settle();
            },
            onThrowComplete: function () {
                carousel.settle();
            },
            onRelease: function () {
                carousel.autoplayPaused = false;
            },
        });
    }

    settle() {
        const x = gsap.getProperty(this.wrapper, "x");
        this.position = Math.round(-x / this.slideWidth);
        this.normalise();
        this.afterMove();
        this.restartAutoplay();
    }

    restartAutoplay() {
        this.stopAutoplay();
        if (this.autoplayDelay <= 0) return;
        if (this.realSlides.length <= this.slidesPerView) return;

        this.autoplayTimer = window.setInterval(
            function () {
                if (this.autoplayPaused) return;
                if (!this.loop && this.position >= this.lastPosition) {
                    this.moveTo(0, true);
                    return;
                }
                this.next();
            }.bind(this),
            this.autoplayDelay,
        );
    }

    stopAutoplay() {
        if (this.autoplayTimer === null) return;
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
    }
}

$(document).ready(function () {
    document.querySelectorAll(".swiper").forEach(function (element) {
        new Carousel(element, {
            loop: true,
            autoplayDelay: 6000,
            pagination: true,
        });
    });

    document.querySelectorAll(".swiper-manual").forEach(function (element) {
        new Carousel(element, {
            scrollbar: true,
        });
    });
});
